import * as React from "react";

import { useLocalization } from "@shared-ui/localization-context";
import { UitkToast } from "uitk-react-toast";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkText } from "uitk-react-text";

import { withStores } from "stores";

import { getMarketingParams } from "./utils/marketingParams";
import { getCustomerStats, MarketingChannel } from "./utils/customerStats";
import { Affiliate, getAffiliateFromSiteId } from "./utils/affiliates";

import { HotwireAffiliateToastProps } from "./typings";

const DISMISS_TIMEOUT = 2000;

export const HotwireAffiliateToast = withStores(
  "context", // unused but needed to satisfy ComponentProps requirements
  "flexModuleModelStore",
  "hotwireConfig",
  "routeStore"
)(({ flexModuleModelStore, hotwireConfig, routeStore, flexTemplateNode }: HotwireAffiliateToastProps) => {
  const [dismissed, setDismissed] = React.useState<boolean>(false);
  const [affiliate, setAffiliate] = React.useState<Affiliate | null>(null);
  const { formatText } = useLocalization();

  React.useEffect(() => {
    // Check affiliate status from Customer Stats API
    const marketingParams = getMarketingParams(routeStore);
    getCustomerStats(marketingParams, hotwireConfig).then((stats) => {
      if (stats.marketingChannel === MarketingChannel.AFFILIATE) {
        setAffiliate(getAffiliateFromSiteId(marketingParams.siteID));
      }
    });
  }, [setAffiliate]);

  /* istanbul ignore if */
  if (!flexTemplateNode || !flexModuleModelStore) {
    return null;
  }

  // Upon dismissing the toast, ensure it doesn't show up again after giving it time to animate out
  // Without this, the state is reset when the window is resized and the toast pops up again.
  const dismiss = () => setTimeout(() => setDismissed(true), DISMISS_TIMEOUT);

  if (!affiliate || dismissed) {
    return null;
  }

  const { logoUrl, name } = affiliate;

  const header = formatText("hotwire.affiliate-toast.header");
  const actionText = formatText("hotwire.affiliate-toast.dismiss");
  const message = formatText("hotwire.affiliate-toast.message", name);

  return (
    <div className="HotwireAffiliateToast" data-testid="HotwireAffiliateToast">
      <UitkToast show actionText={actionText} onDismiss={dismiss} actionCallback={dismiss}>
        <UitkLayoutFlex space="three" alignItems="center" alignContent="center">
          <UitkLayoutFlexItem>
            <img width={48} height={48} alt={name} src={logoUrl} />
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <div>
              <UitkText size={300} weight="bold">
                {header}
              </UitkText>
              <UitkText size={300} weight="regular">
                {message}
              </UitkText>
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkToast>
    </div>
  );
});

export default HotwireAffiliateToast;
